import React, { useEffect, useState } from 'react';
import { Outlet, useParams, useLocation } from 'react-router-dom';
import { FileText, Menu, Plus } from 'lucide-react';
import { Button } from '@/components/shadcn-ui/button';
import Sidebar from '@/components/sharedComponents/Sidebar';
import { ChatTopHeader } from '@/components/chat/ChatTopHeader';
import { useProjectStore } from '@/stores/useProject.store';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/shadcn-ui/dialog';
import { Input } from '@/components/shadcn-ui/input';
import { Textarea } from '@/components/shadcn-ui/textarea';
import { appAxios } from '@/network/base/appAxios';
import { DialogDescription } from '@radix-ui/react-dialog';
import CustomerModal from '@/components/CustomerModal';
import { useUserStore } from '@/stores/useUser.store';
import AuthorizationModal from '@/components/AuthorizationModal';
const { setUserRole } = useUserStore.getState();


const ProjectPage = () => {
  const { permitChatId } = useParams();
  const [modalOpen, setModalOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [showAuthModal, setshowAuthModal] = useState(false);
  const location = useLocation();
  const [NewChat, setNewChat] = useState(false);
  const userRole = useUserStore(state => state.userRole);
  const activePermitChat = useProjectStore(state => state.activePermitChat());
  const projects = useProjectStore(state => state.projects);
  const getProjects = useProjectStore(state => state.getProjects);
  const isNewChat = location.pathname.includes('new-chat');


  useEffect(() => {
    if (isNewChat) {
      setNewChat(true);
    } else {
      setNewChat(false);
    }
  }, [isNewChat]);
  useEffect(() => {
    if (modalOpen && !showForm) {
      getProjects();
    }
  }, [modalOpen, showForm]);

  const getUserRole = async () => {
    try {
      const response = await appAxios.get('/Authorization/v1.0/getUserRole')
      const role = response.data.data;
      setUserRole(role);
      return role;
    } catch (error) {
      console.error('Failed to get user role:', error);
      return null;
    }
  }

  const handleAuthSubmit = async () => {
    const role = await getUserRole();
    setshowAuthModal(false);
    setTimeout(() => {
      if (!role) return;
      setModalOpen(true);
      setShowForm(false);
    }, 0);
  };

  const handleLinkProject = () => {
    if (!userRole) {
      setshowAuthModal(true);
      return;
    }
    setModalOpen(true);
    setShowForm(false);
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await appAxios.post('/Project/v1.0/createProject', {
        projectName: formData.name, projectDescription: formData.description, permitChatId
      });
      await getProjects(true);
      setShowForm(false);
      setFormData({ name: '', description: '' });
    } catch (error) {
      console.error('Failed to create project:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProject = async (projectId) => {
    setLoading(true);
    try {
      await appAxios.post('/Project/v1.0/assignProjectToChat', {
        permitChatId,
        projectId,
      });
      setModalOpen(false);
      window.location.reload();
    } catch (error) {
      console.error('Failed to link project:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    // <div className={`dark flex h-full w-full overflow-hidden font-baloo-2 ${NewChat ? 'bg-figma-background' : ''}`}> 
    <div className={`dark flex h-full w-full overflow-hidden font-baloo-2 bg-figma-background md:bg-secondary`}>
      <Sidebar />

      {/* Main Content Area */}
      {/* <div className={`flex-1 flex flex-col h-full w-full overflow-hidden ${NewChat ? 'bg-transparent md:bg-background-secondary' : 'bg-background-secondary'}`}> */}
      <div className={`flex-1 flex flex-col h-full w-full overflow-hidden bg-transparent md:bg-secondary'}`}>
        {permitChatId && activePermitChat && (
          <ChatTopHeader
            projectAssigned={activePermitChat.projectAssigned}
            onLinkProject={handleLinkProject}
          />
        )}
        <main className="flex-1 h-full w-full overflow-y-auto overflow-x-hidden no-scrollbar m-2">
          <div className="h-full w-full max-w-8xl mx-auto p-4 lg:p-6">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Authorization Modal */}
      <AuthorizationModal
        isOpen={showAuthModal}
        onClose={() => setshowAuthModal(false)}
        onSubmit={() => handleAuthSubmit()}
      />


      {userRole === 'contractor' && (
        <CustomerModal isOpen={modalOpen} onClose={() => setModalOpen(false)} permitChatId={permitChatId} />
      )}
      {userRole === 'home_owner' && (
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="sm:max-w-lg dark text-white">
            <DialogHeader>
              <DialogTitle className="text-xl font-baloo-2 my-1">{showForm ? 'Create New Project' : 'Select a Project'}</DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground">Link this chat to an existing project or create a new one</DialogDescription>
            </DialogHeader>

            {showForm ? (
              <form onSubmit={handleCreateProject} className="space-y-6">
                <div>
                  <Input
                    placeholder="Project Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Textarea
                    placeholder="Project Description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)} disabled={loading}>
                    Back
                  </Button>
                  <Button type="submit" className="flex-1" disabled={loading}>
                    {loading ? 'Creating...' : 'Create Project'}
                  </Button>
                </div>
              </form>
            ) : (
              <div className="space-y-3">
                <Button
                  onClick={() => setShowForm(true)}
                  className="w-full flex items-center gap-2"
                  variant="outline"
                >
                  <Plus size={16} />
                  Create New Project
                </Button>

                <div className="border-t pt-3 max-h-[300px] overflow-y-auto space-y-2 no-scrollbar">
                  {projects.length > 0 ? (
                    projects.map((project) => (
                      <button
                        key={project._id}
                        onClick={() => handleSelectProject(project._id)}
                        disabled={loading}
                        className="
                      w-full text-left
                      p-4 rounded-xl
                      border border-border
                      bg-background
                      hover:bg-accent/60 hover:border-accent
                      focus:outline-none focus:ring-2 focus:ring-primary/40
                      transition
                      disabled:opacity-50 disabled:cursor-not-allowed
                    "
                      >
                        <div className="flex items-start gap-3">
                          {/* Icon */}
                          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary shrink-0">
                            <FileText size={18} />
                          </div>

                          {/* Content */}
                          <div className="flex flex-col gap-1 min-w-0">
                            <div className="font-medium leading-tight truncate">
                              {project.projectName}
                            </div>

                            {project.projectDescription && (
                              <div className="text-sm text-muted-foreground line-clamp-2">
                                {project.projectDescription}
                              </div>
                            )}
                          </div>
                        </div>
                      </button>

                    ))
                  ) : (
                    <div className="text-center text-sm text-muted-foreground py-4">No projects available</div>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default ProjectPage;
